/**
 * One on/off platform flag backed by a GET/PUT `{ enabled }` admin endpoint
 * (chat streaming, analysis streaming, agentic chat).
 *
 * `load` is called from usePlatformAdminData so the flags are fetched with the
 * rest of the platform data (before the loading spinner clears, and again on
 * pull-to-refresh).
 */
import { useCallback, useState } from 'react';
import { apiRequest } from '../../../../services/apiClient';
import { showDialog } from '../../../../utils/dialogs';

export interface FeatureToggleConfig {
  endpoint: string;
  loadErrorLog: string;
  toggleErrorLog: string;
  toggleErrorMessage: string;
}

export const CHAT_STREAMING_TOGGLE: FeatureToggleConfig = {
  endpoint: '/admin/chat-streaming',
  loadErrorLog: 'Failed to load chat streaming settings',
  toggleErrorLog: 'Failed to toggle streaming:',
  toggleErrorMessage: 'Failed to update streaming setting',
};

export const ANALYSIS_STREAMING_TOGGLE: FeatureToggleConfig = {
  endpoint: '/admin/analysis-streaming',
  loadErrorLog: 'Failed to load analysis streaming settings',
  toggleErrorLog: 'Failed to toggle analysis streaming:',
  toggleErrorMessage: 'Failed to update analysis streaming setting',
};

export const AGENTIC_CHAT_TOGGLE: FeatureToggleConfig = {
  endpoint: '/admin/agentic-chat',
  loadErrorLog: 'Failed to load agentic chat settings',
  toggleErrorLog: 'Failed to toggle agentic chat:',
  toggleErrorMessage: 'Failed to update agentic chat setting',
};

export interface FeatureToggleState {
  enabled: boolean;
  loading: boolean;
  load: () => Promise<void>;
  toggle: () => Promise<void>;
}

export function useFeatureToggle(config: FeatureToggleConfig): FeatureToggleState {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const settings = await apiRequest<{ enabled: boolean }>({ endpoint: config.endpoint });
      setEnabled(settings.enabled);
    } catch (error) {
      console.warn(config.loadErrorLog, error);
    }
  }, [config]);

  const toggle = async () => {
    setLoading(true);
    try {
      const result = await apiRequest<{ enabled: boolean }>({
        endpoint: config.endpoint,
        method: 'PUT',
        data: { enabled: !enabled },
      });
      setEnabled(result.enabled);
    } catch (error) {
      console.error(config.toggleErrorLog, error);
      showDialog('Error', config.toggleErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return { enabled, loading, load, toggle };
}
